import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Legend,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { useFetch } from "../../../hooks/use-fetch";
import { BASE_ADMIN_API_URL } from "../../../config";
import { parseCsv } from "../csv";
import { analyzeOperations } from "./analyze-operations";
import dayjs from "dayjs";

Chart.register(CategoryScale, LinearScale, BarElement, Title, Legend, Tooltip);

function KpiCard({ value, label, color, sub }) {
  return (
    <Card sx={{ flex: 1, textAlign: "center" }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="h4" fontWeight={700} color={color || "primary"}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        {sub && (
          <Typography variant="body2" color="text.secondary">
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

const trendChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { mode: "index" },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { stepSize: 1, precision: 0 },
      title: { display: true, text: "Nouveaux contacts" },
    },
    x: { ticks: { maxRotation: 45 } },
  },
};

const channelChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: "y",
  plugins: {
    legend: { position: "top" },
    tooltip: { mode: "index" },
  },
  scales: {
    x: {
      beginAtZero: true,
      ticks: { stepSize: 1, precision: 0 },
    },
  },
};

export default function OperationsPage() {
  const [snapshots, setSnapshots] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [csvData, setCsvData] = useState({});
  const fetchWithAuth = useFetch();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth(`${BASE_ADMIN_API_URL}/crm/snapshots`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        if (!cancelled) {
          setSnapshots(data);
          if (data.length > 0) setSelectedId(data[0].id);
        }
      } catch (e) {
        console.error("Failed to load snapshots", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchWithAuth]);

  const loadCsvData = useCallback(
    async (snapshotId) => {
      if (!snapshotId) return;
      setLoadingData(true);
      setCsvData({});
      try {
        const boards = ["CONTACTS", "CONTACTS_PRO", "TESTS", "CLIENTS"];
        const results = {};
        for (const board of boards) {
          const res = await fetchWithAuth(
            `${BASE_ADMIN_API_URL}/crm/snapshots/${snapshotId}/files/${board}`
          );
          if (res.ok) {
            const text = await res.text();
            results[board] = parseCsv(text);
          }
        }
        setCsvData(results);
      } catch (e) {
        console.error("Failed to load CSV data", e);
      } finally {
        setLoadingData(false);
      }
    },
    [fetchWithAuth]
  );

  useEffect(() => {
    if (selectedId) loadCsvData(selectedId);
  }, [selectedId, loadCsvData]);

  const analysis = useMemo(() => {
    if (!csvData.CONTACTS && !csvData.CONTACTS_PRO && !csvData.TESTS && !csvData.CLIENTS)
      return null;
    return analyzeOperations({
      contactsRows: csvData.CONTACTS || [],
      contactsProRows: csvData.CONTACTS_PRO || [],
      testsRows: csvData.TESTS || [],
      clientsRows: csvData.CLIENTS || [],
    });
  }, [csvData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (snapshots.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography
            color="text.secondary"
            sx={{ textAlign: "center", py: 4 }}
          >
            Aucun snapshot CRM disponible.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const deltaSign = analysis && analysis.newContacts.delta > 0 ? "+" : "";
  const deltaColor =
    analysis && analysis.newContacts.delta > 0
      ? "success.main"
      : analysis && analysis.newContacts.delta < 0
        ? "error.main"
        : "text.secondary";

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardHeader
          title="Operations"
          subheader="Pilotage mensuel de l'activité"
          action={
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Snapshot</InputLabel>
              <Select
                value={selectedId}
                label="Snapshot"
                onChange={(e) => setSelectedId(e.target.value)}
              >
                {snapshots.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {dayjs(s.created_at).format("YYYY-MM-DD HH:mm")}
                    {" — "}
                    {s.files.reduce((sum, f) => sum + (f.row_count || 0), 0)}{" "}
                    lignes
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        />
      </Card>

      {loadingData || !analysis ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* === Acquisition === */}
          <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
            Acquisition
          </Typography>

          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Nouveaux contacts — {analysis.currentMonth}
          </Typography>

          <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
            <KpiCard
              value={analysis.newContacts.current}
              label={analysis.currentMonth}
              color="primary"
            />
            <KpiCard
              value={analysis.newContacts.previous}
              label={analysis.prevMonth}
              color="text.secondary"
            />
            <KpiCard
              value={`${deltaSign}${analysis.newContacts.delta}`}
              label="Delta"
              color={deltaColor}
            />
          </Stack>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Bar
                height={300}
                data={{
                  labels: analysis.newContacts.trendLabels,
                  datasets: [
                    {
                      label: "Nouveaux contacts",
                      data: analysis.newContacts.trendData,
                      backgroundColor: analysis.newContacts.trendData.map(
                        (_, i) =>
                          i === analysis.newContacts.trendData.length - 1
                            ? "rgba(99,102,241,.9)"
                            : "rgba(99,102,241,.4)"
                      ),
                      borderRadius: 4,
                    },
                  ],
                }}
                options={trendChartOptions}
              />
            </CardContent>
          </Card>

          {/* === KPI 2: Channels === */}
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            Canaux d'acquisition
          </Typography>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Bar
                height={Math.max(200, analysis.channels.labels.length * 35)}
                data={{
                  labels: analysis.channels.labels,
                  datasets: [
                    {
                      label: analysis.channels.currentMonth,
                      data: analysis.channels.currentData,
                      backgroundColor: "rgba(99,102,241,.8)",
                      borderRadius: 4,
                    },
                    {
                      label: analysis.channels.prevMonth,
                      data: analysis.channels.prevData,
                      backgroundColor: "rgba(209,213,219,.6)",
                      borderRadius: 4,
                    },
                  ],
                }}
                options={channelChartOptions}
              />
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Canal</TableCell>
                      <TableCell align="right">
                        {analysis.channels.currentMonth}
                      </TableCell>
                      <TableCell align="right">
                        {analysis.channels.prevMonth}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analysis.channels.labels.map((ch, i) => (
                      <TableRow key={ch} hover>
                        <TableCell>{ch}</TableCell>
                        <TableCell align="right">
                          {analysis.channels.currentData[i]}
                        </TableCell>
                        <TableCell align="right">
                          {analysis.channels.prevData[i]}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableBody>
                    <TableRow sx={{ fontWeight: 600 }}>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {analysis.newContacts.current}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {analysis.newContacts.previous}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* === Activation === */}
          <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
            Activation
          </Typography>

          <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
            <KpiCard
              value={analysis.activation.ongoing.length}
              label="Essais en cours"
              color="success.main"
            />
            <KpiCard
              value={analysis.activation.waiting.length}
              label="En attente"
              color="warning.main"
            />
            <KpiCard
              value={analysis.activation.startingSoon.length}
              label="Démarrent sous 2 sem."
            />
            <KpiCard
              value={analysis.activation.endingSoon.length}
              label="Se terminent sous 2 sem."
            />
          </Stack>

          {analysis.activation.ongoing.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Essais en cours
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nom</TableCell>
                        <TableCell>Début</TableCell>
                        <TableCell>Fin</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysis.activation.ongoing.map((t) => (
                        <TableRow key={t.name} hover>
                          <TableCell>{t.name}</TableCell>
                          <TableCell>{t.start}</TableCell>
                          <TableCell>{t.end}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {analysis.activation.waiting.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  En attente
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nom</TableCell>
                        <TableCell>Date de demande</TableCell>
                        <TableCell>Début prévu</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysis.activation.waiting.map((t) => (
                        <TableRow key={t.name} hover>
                          <TableCell>{t.name}</TableCell>
                          <TableCell>{t.demande}</TableCell>
                          <TableCell>{t.debut || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {analysis.activation.startingSoon.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Démarrent sous 2 semaines
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nom</TableCell>
                        <TableCell>Date de début</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysis.activation.startingSoon.map((t) => (
                        <TableRow key={t.name + t.date} hover>
                          <TableCell>{t.name}</TableCell>
                          <TableCell>{t.date}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {analysis.activation.endingSoon.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Se terminent sous 2 semaines
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nom</TableCell>
                        <TableCell>Date de fin</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysis.activation.endingSoon.map((t) => (
                        <TableRow key={t.name + t.date} hover>
                          <TableCell>{t.name}</TableCell>
                          <TableCell>{t.date}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* === Revenue === */}
          <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
            Revenue
          </Typography>

          <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
            <KpiCard
              value={analysis.revenue.installCurrent}
              label={`Installations ${analysis.currentMonth}`}
              color="success.main"
            />
            <KpiCard
              value={analysis.revenue.installPrev}
              label={analysis.prevMonth}
              color="text.secondary"
            />
            <KpiCard
              value={analysis.revenue.concludedToContact.length}
              label="Concluants à contacter"
              color="warning.main"
            />
            <KpiCard
              value={analysis.revenue.noStatus.length}
              label="Essais sans statut"
              color="error.main"
            />
          </Stack>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Bar
                height={300}
                data={{
                  labels: analysis.revenue.installTrendLabels,
                  datasets: [
                    {
                      label: "Installations",
                      data: analysis.revenue.installTrendData,
                      backgroundColor: analysis.revenue.installTrendData.map(
                        (_, i) =>
                          i === analysis.revenue.installTrendData.length - 1
                            ? "rgba(16,185,129,.9)"
                            : "rgba(16,185,129,.4)"
                      ),
                      borderRadius: 4,
                    },
                  ],
                }}
                options={{
                  ...trendChartOptions,
                  scales: {
                    ...trendChartOptions.scales,
                    y: {
                      ...trendChartOptions.scales.y,
                      title: { display: true, text: "Installations" },
                    },
                  },
                }}
              />
            </CardContent>
          </Card>

          {analysis.revenue.newInstallations.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Installations — {analysis.currentMonth}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nom</TableCell>
                        <TableCell>Date d'installation</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysis.revenue.newInstallations.map((t) => (
                        <TableRow key={t.name} hover>
                          <TableCell>{t.name}</TableCell>
                          <TableCell>{t.date}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {analysis.revenue.concludedToContact.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Essais concluants — pas encore client
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nom</TableCell>
                        <TableCell>Période d'essai</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysis.revenue.concludedToContact.map((t) => (
                        <TableRow key={t.name} hover>
                          <TableCell>{t.name}</TableCell>
                          <TableCell>{t.periode || "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {analysis.revenue.noStatus.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Essais terminés sans statut
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Nom</TableCell>
                        <TableCell>Fin d'essai</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analysis.revenue.noStatus.map((t) => (
                        <TableRow key={t.name} hover>
                          <TableCell>{t.name}</TableCell>
                          <TableCell>{t.end}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
